/**
 * Camera Control — zoom / follow-speed / deadzone tuning with persistence.
 * Uses useCameraSettings hook for state management (SRP).
 *
 * Engine window (HUD fold, 2026-06-12): the registry provides the ToolWindow
 * chrome; this is the propless Content body. CameraSystem comes from the
 * ServiceRegistry (HUD used to thread it as a prop).
 */

import React from 'react';
import { useCameraSettings } from '../../systems/camera/useCameraSettings';
import { ServiceRegistry } from '../../core/ServiceRegistry';

export const CameraControlContent: React.FC = () => {
  const registry = ServiceRegistry.getInstance();
  const cameraSystem = registry.has('CameraSystem')
    ? registry.get('CameraSystem')!
    : null;
  // Use the settings hook (single responsibility: state management)
  const {
    settings,
    updateSetting,
    resetSettings,
    profiles,
    currentProfileName,
    setCurrentProfileName,
    saveProfile,
    loadProfile,
    deleteProfile,
    exportSettings,
    lastAction
  } = useCameraSettings({ cameraManager: cameraSystem?.getManager() ?? null });

  const hasProfiles = Object.keys(profiles).length > 0;

  return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '12px' }}>
        
        {/* Zoom */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', textTransform: 'uppercase', opacity: 0.6, marginBottom: '6px' }}>
            <span>Zoom</span>
            <span>{settings.zoom.toFixed(2)}x</span>
          </div>
          <input
            type="range"
            min="0.25"
            max="4"
            step="0.05"
            value={settings.zoom}
            onChange={(e) => updateSetting('zoom', parseFloat(e.target.value))}
            style={{ width: '100%', height: '20px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', opacity: 0.4, marginTop: '2px' }}>
            <span>0.25x</span>
            <span>1x</span>
            <span>4x</span>
          </div>
        </div>

        {/* Follow Speed */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', textTransform: 'uppercase', opacity: 0.6, marginBottom: '4px' }}>
            <span>Follow Speed</span>
            <span>{(settings.followSpeed * 100).toFixed(0)}%</span>
          </div>
          <input
            type="range"
            min="0.01"
            max="1"
            step="0.01"
            value={settings.followSpeed}
            onChange={(e) => updateSetting('followSpeed', parseFloat(e.target.value))}
            style={{ width: '100%', height: '16px' }}
          />
        </div>

        {/* Deadzone */}
        <div>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.6, marginBottom: '4px' }}>
            Deadzone
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '9px', opacity: 0.5 }}>W</div>
              <input
                type="number"
                value={settings.deadzoneWidth}
                onChange={(e) => updateSetting('deadzoneWidth', parseInt(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: '4px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '3px',
                  color: 'white',
                  fontSize: '11px'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '9px', opacity: 0.5 }}>H</div>
              <input
                type="number"
                value={settings.deadzoneHeight}
                onChange={(e) => updateSetting('deadzoneHeight', parseInt(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: '4px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '3px',
                  color: 'white',
                  fontSize: '11px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Axis Lock */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <label style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px'
          }}>
            <input
              type="checkbox"
              checked={settings.lockX}
              onChange={(e) => updateSetting('lockX', e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Lock X
          </label>
          <label style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px'
          }}>
            <input
              type="checkbox"
              checked={settings.lockY}
              onChange={(e) => updateSetting('lockY', e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Lock Y
          </label>
        </div>

        {/* Profiles Section */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.6, marginBottom: '8px', fontWeight: 'bold' }}>
            Camera Profiles
          </div>
          
          {/* Profile Name Input + Save */}
          <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
            <input 
              placeholder="Profile Name"
              style={{
                flex: 1,
                padding: '6px',
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '4px',
                color: 'white',
                fontSize: '12px'
              }}
              value={currentProfileName}
              onChange={(e) => setCurrentProfileName(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <button 
              style={{
                padding: '6px 12px',
                background: '#27ae60',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold'
              }}
              onClick={() => saveProfile(currentProfileName)}
            >
              Save
            </button>
          </div>

          {/* Profile List */}
          <div style={{ 
            maxHeight: '120px', 
            overflowY: 'auto', 
            background: 'rgba(0,0,0,0.2)', 
            padding: '5px', 
            borderRadius: '5px',
            marginBottom: '8px'
          }}>
            {!hasProfiles ? (
              <div style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '8px' }}>
                No saved profiles
              </div>
            ) : (
              Object.keys(profiles).map(name => (
                <div 
                  key={name} 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '4px 0', 
                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <span style={{ fontSize: '11px', color: '#eee' }}>{name}</span>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button 
                      style={{ 
                        padding: '2px 6px', 
                        fontSize: '10px', 
                        background: '#2980b9',
                        border: 'none',
                        borderRadius: '3px',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => loadProfile(name)}
                    >
                      Load
                    </button>
                    <button 
                      style={{ 
                        padding: '2px 6px', 
                        fontSize: '10px', 
                        background: '#e74c3c',
                        border: 'none',
                        borderRadius: '3px',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => deleteProfile(name)}
                    >
                      Del
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={resetSettings}
            style={{
              flex: 1,
              padding: '8px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            Reset
          </button>
          <button
            onClick={exportSettings}
            style={{
              flex: 1,
              padding: '8px',
              background: '#e67e22',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            Export
          </button>
        </div>

        {/* Last Action Log */}
        <div style={{ 
          padding: '6px 8px',
          background: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
          fontSize: '10px',
          fontFamily: 'monospace',
          color: '#888'
        }}>
          {lastAction}
        </div>

      </div>
  );
};
